import { useMemo, useState, type FormEvent } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useBlockAvailabilityRange } from '@/features/guide/hooks/useGuideAvailability';

function getTodayDate() {
  return new Date().toISOString().slice(0, 10);
}

function getRangeDays(from: string, to: string) {
  const fromDate = new Date(`${from}T00:00:00.000Z`);
  const toDate = new Date(`${to}T00:00:00.000Z`);
  return Math.floor((toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
}

interface AddLeaveFormProps {
  onSuccess?: () => void;
  usePaper?: boolean;
}

export function AddLeaveForm({ onSuccess, usePaper = true }: AddLeaveFormProps) {
  const today = useMemo(getTodayDate, []);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [notes, setNotes] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const blockRange = useBlockAvailabilityRange();

  const mutationError = (blockRange.error as Error | null)?.message ?? null;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSuccessMessage(null);
    setLocalError(null);

    if (!from || !to) {
      setLocalError('Please select both start and end dates.');
      return;
    }
    if (from < today) {
      setLocalError('Past dates cannot be blocked.');
      return;
    }
    if (to < from) {
      setLocalError('End date must be on or after start date.');
      return;
    }

    const totalDays = getRangeDays(from, to);
    if (totalDays > 90) {
      setLocalError('You can block at most 90 days at a time.');
      return;
    }

    await blockRange.mutateAsync({
      from,
      to,
      notes: notes.trim() || undefined,
    });

    setFrom(today);
    setTo(today);
    setNotes('');
    setSuccessMessage(
      totalDays === 1 ? 'Leave date blocked successfully.' : `${totalDays} leave dates blocked successfully.`
    );
    onSuccess?.();
  };

  const content = (
    <>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.75 }}>
        Block leave dates
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Use this only for personal leave. Booking dates are blocked automatically after a traveler pays.
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
          <TextField
            label="From"
            type="date"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: today }}
            fullWidth
          />
          <TextField
            label="To"
            type="date"
            value={to}
            onChange={(event) => setTo(event.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: from || today }}
            fullWidth
          />
        </Box>

        <TextField
          label="Reason or note (optional)"
          placeholder="For example: family function, festival leave, personal travel"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          multiline
          rows={3}
          fullWidth
          inputProps={{ maxLength: 500 }}
        />

        {(localError || mutationError) && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {localError || mutationError}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {successMessage}
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button type="submit" variant="contained" disabled={blockRange.isPending}>
            {blockRange.isPending ? 'Blocking...' : 'Block dates'}
          </Button>
        </Box>
      </Box>
    </>
  );

  if (!usePaper) {
    return content;
  }

  return (
    <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
      {content}
    </Paper>
  );
}
